<script lang="ts">
  import AuthorPanel from '$lib/components/website/AuthorPanel.svelte';
  import Video from '$lib/components/control/Video.svelte';
  import Carousel from 'svelte-carousel';
  import CtaPanel from '$lib/components/website/ctaPanel/CtaPanel.svelte';
  import Badge from '$lib/components/control/Badge.svelte';
  import CardPanel from '$lib/components/website/CardPanel.svelte';
  import Step from '$lib/components/website/exercise-page/Step.svelte';
  import { CARD_PANEL_VARIANT, CARD_PANEL_HEADING_ALIGNMENT } from '$lib/constants/constants';
  import { onMount } from 'svelte';
  import ThemedHeader from '$lib/components/website/ThemedHeader.svelte';
  import ShareButton from '$lib/components/website/ShareButton.svelte';
  import Breadcrumbs from '$lib/components/website/Breadcrumbs.svelte';
  import { MUSCLE_GROUPS } from '$lib/constants/constants';

  export let data;

  let width;
  let videoHeight;
  let isMounted = false;

  onMount(() => {
    isMounted = true;
  });
</script>

<svelte:window bind:innerWidth={width} />
<ThemedHeader
  backgroundColor="md:bg-gray-700"
  customClass="absolute h-[18.75rem] sm:h-[20.125rem] left-0 right-0 -z-10"
/>
<div class="flex flex-col justify-center items-center">
  <article class="flex flex-col items-center max-w-1280 lg:py-8">
    <div
      class="max-w-[50rem] border-0 border-transparent lg:rounded-xl overflow-hidden"
      style="width: {width}px"
    >
      {#if isMounted}
        <Carousel arrows={false} infinite={false} dots={true}>
          <div class="mb-4" bind:clientHeight={videoHeight}>
            <Video video={data?.pageData?.exerciseVideo} />
          </div>
          {#if data?.pageData?.bodyAreaImagesCollection?.items.length}
            <div class="bg-white relative overflow-hidden w-max" style="height: {videoHeight}px">
              <img
                src={MUSCLE_GROUPS.imageSrc}
                alt={MUSCLE_GROUPS.alt}
                class="absolute object-contain w-full h-full"
              />
              {#each data?.pageData?.bodyAreaImagesCollection?.items as bodyAreaImage}
                <img
                  src="{bodyAreaImage?.url}?w=800&fm=webp"
                  alt={bodyAreaImage?.title}
                  class="absolute object-contain w-full h-full"
                />
              {/each}
            </div>
          {/if}
        </Carousel>
      {/if}
    </div>

    <div class="flex flex-col items-center py-8 px-4 gap-4 w-full">
      <div>
        <Breadcrumbs
          links={[
            { label: 'Sweat', href: '/' },
            { label: 'Exercises', href: '/exercises' },
            {
              label: data?.pageData?.exerciseName,
              href: `/exercises${data?.pageData?.slug.toLowerCase()}`,
              isCurrentUrl: true,
            },
          ]}
          currentTextColor="text-primary-500"
          textColor="text-primary-200"
          chevronColor="#FFCCDD"
        />
      </div>
      <h1 class="text-display-md text-gray-900 font-bold text-center lg:text-display-xl">
        {data?.pageData?.exerciseName}
      </h1>
    </div>
    {#if data?.pageData?.shortDescription !== null}
      <p
        class="text-md text-gray-500 font-medium max-w-800 mb-8 px-4 text-center lg:py-8 lg:mb-4 lg:text-xxl lg:px-0"
      >
        {data?.pageData?.shortDescription}
      </p>
    {/if}
    <div class="pb-16 w-full flex flex-col gap-12 px-4 max-w-800 lg:px-0 lg:pt-8 lg:gap-16">
      {#if data?.pageData?.step1}
        <Step stepTitle="Step 1" stepDescription={data?.pageData?.step1} />
      {/if}
      {#if data?.pageData?.step2}
        <Step stepTitle="Step 2" stepDescription={data?.pageData?.step2} />
      {/if}
      {#if data?.pageData?.step3}
        <Step stepTitle="Step 3" stepDescription={data?.pageData?.step3} />
      {/if}
      {#if data?.pageData?.step4}
        <Step stepTitle="Step 4" stepDescription={data?.pageData?.step4} />
      {/if}
      {#if data?.pageData?.step5}
        <Step stepTitle="Step 5" stepDescription={data?.pageData?.step5} />
      {/if}
      {#if data?.pageData?.step6}
        <Step stepTitle="Step 6" stepDescription={data?.pageData?.step6} />
      {/if}
      {#if data?.pageData?.step7}
        <Step stepTitle="Step 7" stepDescription={data?.pageData?.step7} />
      {/if}
      {#if data?.pageData?.step8}
        <Step stepTitle="Step 8" stepDescription={data?.pageData?.step8} />
      {/if}
    </div>
    <div class="mt-8 px-4 w-full max-w-800 lg:p-0">
      {#if data?.pageData?.trainer}
        <AuthorPanel authorPanel={data?.pageData?.trainer} />
      {/if}
      <div
        class="py-6 flex flex-col gap-6 lg:flex-row lg:gap-12 lg:justify-between lg:items-center"
      >
        <div class="flex flex-row gap-2 flex-wrap">
          {#if data?.pageData?.tags?.length > 0}
            {#each data?.pageData?.tags as tag}
              <Badge text={tag} />
            {/each}
          {/if}
        </div>
        <div>
          <ShareButton />
        </div>
      </div>
    </div>
  </article>
  {#if data?.pageData?.alternateExerciseCollection?.items?.length > 0}
    <section
      class="p-4 flex flex-col justify-center w-full max-w-[440px] sm:max-w-1280 lg:px-8 lg:py-0"
    >
      <CardPanel
        heading="Alternates for this exercise"
        subHeading="Exercises"
        cardsCollection={data?.pageData?.alternateExercises}
        variant={CARD_PANEL_VARIANT.SLIDER}
        headingAlignment={CARD_PANEL_HEADING_ALIGNMENT.CENTER}
      />
    </section>
  {/if}
  {#if data?.pageData?.ctaPanel}
    <section class="w-full py-8 my-8 lg:py-4 lg:my-16">
      <CtaPanel ctaPanel={data?.pageData?.ctaPanel} />
    </section>
  {/if}
  {#if data?.pageData?.relatedExerciseContentCollection?.items?.length > 0}
    <section
      class="p-4 flex flex-col justify-center w-full max-w-[440px] sm:max-w-1280 lg:px-8 lg:py-0 lg:mb-24"
    >
      <CardPanel
        heading="More exercises like this"
        subHeading="Exercises"
        cardsCollection={data?.pageData?.relatedExercises}
        variant={CARD_PANEL_VARIANT.SLIDER}
        headingAlignment={CARD_PANEL_HEADING_ALIGNMENT.CENTER}
      />
    </section>
  {/if}
</div>
